import React, { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  Camera, Strategy, Repeat, Lightbulb, Handshake,
  MagnifyingGlass, PaintBrush, TrendUp, Quotes, CheckCircle,
} from "@phosphor-icons/react";
import { reveal, container } from "../lib/motion";

const WHY = [
  { icon: Camera, text: "I make people feel comfortable on camera." },
  { icon: Strategy, text: "Strategy always comes before content." },
  { icon: Repeat, text: "I believe consistency beats perfection." },
  { icon: Lightbulb, text: "I simplify marketing so business owners feel confident." },
  { icon: Handshake, text: "I become a creative partner — not just another vendor." },
];

const EXPERTISE = [
  "Psychology", "Marketing Strategy", "Brand Design", "Website Design",
  "Photography", "Videography", "Social Media", "Email Marketing",
  "AI Systems", "Content Creation", "Creative Direction", "Business Consulting",
];

const PROCESS = [
  { n: "01", icon: MagnifyingGlass, title: "Discover", desc: "Learn your business, goals, audience, and what makes you different." },
  { n: "02", icon: Strategy, title: "Strategize", desc: "Create a marketing plan with intention instead of guessing." },
  { n: "03", icon: PaintBrush, title: "Create", desc: "Produce photography, video, branding, websites, and content that reflects your business." },
  { n: "04", icon: TrendUp, title: "Grow", desc: "Measure results, improve continuously, and become your long-term creative partner." },
];

const STATS = [
  { to: 40, suffix: "+", label: "Businesses Helped" },
  { to: 120, suffix: "+", label: "Projects Completed" },
  { to: 6, suffix: "+", label: "Years of Experience" },
  { to: 12, suffix: "", label: "Creative Services Offered" },
];

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1500;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return <span ref={ref}>{val}{suffix}</span>;
};

const Block = ({ label, children }) => (
  <motion.div variants={reveal}>
    <span className="mb-4 block text-xs font-bold uppercase tracking-[0.22em] text-teal">{label}</span>
    {children}
  </motion.div>
);

export const About = () => (
  <section id="about" className="relative overflow-hidden bg-gradient-to-b from-white via-mist/50 to-white py-24 md:py-32">
    <div className="pointer-events-none absolute -right-40 top-32 h-[420px] w-[420px] rounded-full bg-teal/10 blur-[130px]" />
    <div className="pointer-events-none absolute -left-40 bottom-40 h-[420px] w-[420px] rounded-full bg-beige/40 blur-[130px]" />

    <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
      {/* Heading */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }} className="mb-16 max-w-3xl">
        <motion.span variants={reveal} className="mb-5 block text-xs font-bold uppercase tracking-[0.25em] text-teal">
          About
        </motion.span>
        <motion.h2 variants={reveal} className="font-display text-5xl font-black leading-[0.95] tracking-tight text-navy md:text-7xl" data-testid="about-headline">
          Meet Stefanie
        </motion.h2>
        <motion.p variants={reveal} className="mt-5 font-sans text-lg leading-relaxed text-zinc-600 md:text-xl">
          Helping businesses tell their story with strategy, creativity, and purpose.
        </motion.p>
      </motion.div>

      {/* Two-column: portrait + intro/philosophy */}
      <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
        {/* LEFT — portrait */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto w-full max-w-[380px] lg:sticky lg:top-28 lg:mx-0 lg:self-start"
          data-testid="about-portrait"
        >
          <div className="relative h-[420px] overflow-hidden rounded-3xl shadow-2xl shadow-navy/20 sm:h-[480px] lg:h-[540px]">
            <img src="/stefanie-portrait.png?v=2" alt="Stefanie Herrera, founder of Jakin Creative Design & Marketing" className="h-full w-full object-cover object-top" />
            <div className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/20" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4, duration: 0.6 }}
            className="absolute -bottom-6 right-4 rounded-2xl border border-white/60 bg-white/80 p-5 shadow-xl backdrop-blur-md md:-right-6"
            data-testid="about-role-card"
          >
            {["Founder", "Creative Director", "Marketing Strategist"].map((r) => (
              <div key={r} className="flex items-center gap-2.5 py-1">
                <CheckCircle size={18} weight="fill" className="text-teal" />
                <span className="font-display text-sm font-bold tracking-tight text-navy">{r}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* RIGHT — text blocks */}
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="space-y-12">
          <Block label="Introduction">
            <p className="font-display text-2xl font-bold leading-snug tracking-tight text-navy md:text-3xl">
              Marketing isn't just about creating content. <span className="text-teal">It's about understanding people.</span>
            </p>
            <div className="mt-5 space-y-4 font-sans text-base leading-relaxed text-zinc-600 md:text-lg">
              <p>Hi, I'm <strong className="font-semibold text-navy">Stefanie Herrera</strong>, founder of Jakin Creative Design &amp; Marketing. I help small businesses establish and transform their expertise into marketing that builds trust, attracts the right clients, and creates long-term growth.</p>
              <p>My background combines psychology, coaching, branding, photography, videography, website design, and AI-powered marketing systems — allowing me to create marketing that connects on both a human and strategic level.</p>
            </div>
          </Block>

          <Block label="My Philosophy">
            <div className="space-y-4 font-sans text-base leading-relaxed text-zinc-600 md:text-lg">
              <p>I believe every business has a story worth telling. My role isn't to make you someone you're not — it's to uncover what already makes your business valuable and communicate that clearly through thoughtful strategy, compelling visuals, and authentic storytelling.</p>
              <p>Whether I'm behind the camera, designing your website, or building your marketing strategy, my goal remains the same: help your business become <strong className="font-semibold text-navy">recognizable, trusted, and memorable</strong>.</p>
            </div>
          </Block>
        </motion.div>
      </div>

      {/* Why clients enjoy working with me */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mt-24">
        <motion.h3 variants={reveal} className="mb-10 font-display text-3xl font-black tracking-tight text-navy md:text-4xl">
          Why clients enjoy working with me
        </motion.h3>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map(({ icon: Icon, text }, i) => (
            <motion.div
              key={i} variants={reveal}
              className="group rounded-2xl border border-zinc-200/80 bg-white/70 p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-teal/40 hover:shadow-xl hover:shadow-teal/10"
              data-testid={`why-client-card-${i}`}
            >
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-white transition-colors duration-300 group-hover:bg-teal">
                <Icon size={22} weight="regular" />
              </div>
              <p className="font-display text-lg font-semibold leading-snug tracking-tight text-navy">{text}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Expertise chips */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mt-24">
        <motion.h3 variants={reveal} className="mb-8 font-display text-3xl font-black tracking-tight text-navy md:text-4xl">
          Expertise
        </motion.h3>
        <motion.div variants={reveal} className="flex flex-wrap gap-3">
          {EXPERTISE.map((e) => (
            <span key={e} data-testid={`expertise-${e.toLowerCase().replace(/\s+/g, "-")}`} className="cursor-default rounded-full border border-zinc-200 bg-white px-5 py-2.5 font-sans text-sm font-medium text-navy transition-colors duration-300 hover:border-teal hover:bg-teal hover:text-white">
              {e}
            </span>
          ))}
        </motion.div>
      </motion.div>

      {/* Process */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mt-24">
        <motion.h3 variants={reveal} className="mb-10 font-display text-3xl font-black tracking-tight text-navy md:text-4xl">
          My process
        </motion.h3>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {PROCESS.map(({ n, icon: Icon, title, desc }, i) => (
            <motion.div key={n} variants={reveal} className="relative">
              <div className="group h-full rounded-2xl bg-navy p-7 text-white transition-transform duration-300 hover:-translate-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-teal transition-colors duration-300 group-hover:bg-teal group-hover:text-white">
                    <Icon size={22} weight="regular" />
                  </div>
                  <span className="font-display text-4xl font-black tracking-tighter text-white/15">{n}</span>
                </div>
                <h4 className="mt-6 font-display text-xl font-bold tracking-tight">{title}</h4>
                <p className="mt-2 font-sans text-sm leading-relaxed text-white/70">{desc}</p>
              </div>
              {i < PROCESS.length - 1 && (
                <span className="absolute -right-3 top-1/2 z-10 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-teal text-white lg:flex">
                  <TrendUp size={12} weight="bold" className="rotate-0" style={{ transform: "rotate(0deg)" }} />
                </span>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Personal note / quote */}
      <motion.div
        initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative mt-24 overflow-hidden rounded-3xl bg-navy p-10 md:p-16"
        data-testid="about-quote"
      >
        <div className="pointer-events-none absolute -right-10 -top-16 h-64 w-64 rounded-full bg-teal/20 blur-[90px]" />
        <Quotes size={80} weight="fill" className="text-teal/40" />
        <p className="mt-4 max-w-4xl font-display text-2xl font-bold leading-snug tracking-tight text-white md:text-4xl md:leading-[1.15]">
          I don't want to become another marketing agency. I want to become the creative partner businesses trust when they're ready to <span className="text-teal">grow with intention.</span>
        </p>
        <p className="mt-8 font-display text-sm font-bold uppercase tracking-[0.2em] text-white/50">— Stefanie Herrera</p>
      </motion.div>

      {/* Stats */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mt-20 grid grid-cols-2 gap-6 border-t border-zinc-200 pt-14 lg:grid-cols-4">
        {STATS.map((s) => (
          <motion.div key={s.label} variants={reveal} className="text-center lg:text-left" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="font-display text-5xl font-black tracking-tighter text-navy md:text-6xl">
              <Counter to={s.to} suffix={s.suffix} />
            </div>
            <div className="mt-2 font-sans text-sm font-medium uppercase tracking-wider text-zinc-500">{s.label}</div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);
